import { CreditCard, Truck, Package, Home as HomeIcon } from "lucide-react";

const Pricing = () => {
  const shippingMethods = [
    {
      title: "GLS Pakkeshop",
      description: "Hent din pakke når det passer dig i din nærmeste GLS pakkeshop.",
      icon: <Package className="w-8 h-8 text-blue-600" />,
      features: ["Fleksibel afhentning", "Over 1.700 steder", "Track & Trace"],
      prices: [
        { weight: "0 – 1 kg", price: "43 kr." },
        { weight: "1 – 2 kg", price: "53 kr." },
        { weight: "2 – 5 kg", price: "79 kr." },
        { weight: "5 – 10 kg", price: "109 kr." },
        { weight: "10 – 15 kg", price: "129 kr." },
        { weight: "15 – 20 kg", price: "135 kr." }
      ]
    },
    {
      title: "GLS Hjemme- & Erhvervslevering",
      description: "Få din pakke leveret direkte til din dør eller erhvervsadresse.",
      icon: <HomeIcon className="w-8 h-8 text-blue-600" />,
      features: ["Hurtig levering", "Direkte til døren", "Track & Trace"],
      prices: [
        { weight: "0 – 1 kg", price: "59 kr." },
        { weight: "1 – 2 kg", price: "69 kr." },
        { weight: "2 – 5 kg", price: "94 kr." },
        { weight: "5 – 10 kg", price: "124 kr." },
        { weight: "10 – 15 kg", price: "164 kr." },
        { weight: "15 – 20 kg", price: "174 kr." }
      ]
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-slate-50 overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tighter text-slate-900 mb-6">
              Priser & <span className="text-blue-600">Fragt</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Her finder du information om betaling og vores leveringsmuligheder med GLS. Vi sørger for, at dine 3D-printede emner kommer sikkert frem.
            </p>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-100 -skew-x-12 transform translate-x-20 hidden lg:block"></div>
      </section>

      {/* Payment Section */}
      <section className="section-padding border-b border-slate-100">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider mb-6">
              <CreditCard size={14} />
              Betaling
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Nem betaling med MobilePay
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Vi gør det nemt og hurtigt at betale for dine bestillinger. Du kan benytte MobilePay til alle køb, både ved standardvarer og specialopgaver.
            </p>
            
            <div className="bg-slate-50 p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 inline-block">
              <div className="flex flex-col gap-2">
                <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">MobilePay Nummer</span>
                <span className="text-4xl font-bold text-slate-900 tracking-tight">9796ZH</span>
                <span className="text-slate-500 font-medium">(HJ3DDesign)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 text-slate-900 text-xs font-bold uppercase tracking-wider mb-6">
              <Truck size={14} />
              Levering
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              Sikker fragt med GLS
            </h2>
            <p className="text-slate-600">
              Vi sender alle vores pakker med GLS for at sikre en hurtig og sporbar levering til hele landet. Herunder kan du se priserne baseret på pakkevægt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {shippingMethods.map((method, index) => (
              <div key={index} className="bg-white p-8 lg:p-12 border border-slate-200 hover:border-blue-600 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 flex flex-col justify-between">
                <div>
                  <div className="mb-6 group-hover:scale-110 transition-transform duration-300 inline-block">
                    {method.icon}
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">{method.title}</h3>
                  <p className="text-slate-500 text-sm mb-6">{method.description}</p>
                  
                  {/* Price Table */}
                  <div className="mb-8 border border-slate-100 rounded-none overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 grid grid-cols-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <span>Vægt</span>
                      <span className="text-right">Pris (inkl. moms)</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {method.prices.map((p, pIndex) => (
                        <div key={pIndex} className="px-4 py-3 grid grid-cols-2 text-sm text-slate-700 hover:bg-slate-50/50 transition-colors">
                          <span className="font-medium">{p.weight}</span>
                          <span className="text-right font-bold text-slate-900">{p.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Fordele</h4>
                  <ul className="space-y-2.5">
                    {method.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3 text-slate-700 text-sm font-medium">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-blue-600 p-8 lg:p-12 text-white flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <h3 className="text-2xl font-semibold mb-2">Har du spørgsmål til din levering?</h3>
              <p className="text-blue-100">
                Kontakt os endelig hvis du har specielle ønsker til fragt eller brug for hjælp til din bestilling.
              </p>
            </div>
            <a 
              href="/kontakt" 
              className="px-8 py-4 bg-white text-blue-600 font-bold uppercase tracking-widest text-xs rounded-none hover:bg-slate-100 transition-colors shrink-0"
            >
              Kontakt os her
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
